import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Coffee } from 'lucide-react';

export default function LoginPage() {
  const { login } = useContext(AuthContext);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      login(response.data.token, response.data.user);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success(response.data.message);
      
      if (response.data.reset_token) {
        // For development - show token
        setResetToken(response.data.reset_token);
        setShowResetForm(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    }
  };

  const handleResetPassword = async () => {
    if (!resetToken || !newPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success('Password reset successful! Please login');
      setShowForgotPassword(false);
      setShowResetForm(false);
      setResetToken('');
      setNewPassword('');
      setForgotEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-stone-100 mobile-container p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-stone-200">
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <div className="bg-gray-800 p-3 rounded-xl mb-4">
              <img 
                src="https://www.themerchantcoffee.co.za/merchant%20white%20logo_No%20Strap%20-1-.png" 
                alt="The Merchant Coffee Logo"
                className="h-12 sm:h-16 w-auto"
              />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-800 text-center" data-testid="login-title">The Merchant Coffee</h1>
            <p className="text-xs sm:text-sm text-stone-600 mt-2 text-center">Staff Scheduling System</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
            <div>
              <Label htmlFor="username" className="text-stone-700">Username</Label>
              <Input
                id="username"
                data-testid="username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="mt-1.5"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-stone-700">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5"
                placeholder="Enter your password"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-button"
              disabled={loading}
              className="w-full bg-gradient-to-r bg-gray-600 hover:from-amber-700 hover:to-orange-700 text-white font-medium py-5 sm:py-6 rounded-xl shadow-md touch-target text-sm sm:text-base"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-4 flex flex-col items-center gap-1.5 text-center">
            <Link to="/forgot-password"
              data-testid="login-forgot-password-link"
              className="text-xs sm:text-sm text-gray-600 hover:text-gray-800 underline touch-target">
              Forgot password?
            </Link>
            <Link to="/forgot-email"
              data-testid="login-forgot-email-link"
              className="text-xs sm:text-sm text-gray-600 hover:text-gray-800 underline touch-target">
              Forgot your email?
            </Link>
          </div>

          <div className="mt-4 sm:mt-6 text-center text-xs text-stone-500">
            Contact your admin for login credentials
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          
          {!showResetForm ? (
            <div className="space-y-4">
              <p className="text-sm text-stone-600">
                Enter your email address to receive a password reset link.
              </p>
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="your-email@example.com"
                />
              </div>
              <Button onClick={handleForgotPassword} className="w-full">
                Send Reset Link
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Development Mode:</strong> Reset token: {resetToken}
                </p>
                <p className="text-xs text-stone-600 mt-1">
                  In production, this would be sent to your email.
                </p>
              </div>
              
              <div>
                <Label>Reset Token</Label>
                <Input
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  placeholder="Enter reset token"
                />
              </div>
              
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
              </div>
              
              <Button onClick={handleResetPassword} className="w-full">
                Reset Password
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
